import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit } from '@angular/core'
import { NavFavoritesService } from '@employer/app/services/nav-favorites.service'
import { EmployerNavigationService, MESSAGES_FAVORITE_ID } from '@employer/app/services/navigation.service'
import { ConfigStore } from '@engineering11/config-web'
import { ConversationStore, openConversationFilter } from '@engineering11/messaging-web'
import { ViewportService } from '@engineering11/web-utilities'
import { ConversationTypes } from 'shared-lib'
import { ExtendedNavId } from '../../layout/main-layout.interface'

// Matches the .nav-messages-container max-height transition in messaging-nav.component.scss.
const EXPAND_TRANSITION_MS = 200

@Component({
  selector: 'custom-messaging-nav',
  styleUrls: ['./messaging-nav.component.scss'],
  // Simple row matching the cnect-ux reference sidebar: outlined mail icon + label + unread badge.
  // Click behavior unchanged (desktop toggles the extended messaging panel; mobile/tablet expands the
  // conversation groups inline). Row look mirrors app-nav-item's .nav-link / .nav-group / .nav-chevron
  // (component-scoped there, so those rules are duplicated in styles below).
  //
  // CNCT-6775 — three changes, all scoped to the inline (below-lg) presentation:
  //   1. Starts collapsed. `isMessagingMenuOpen` is no longer bound to the drawer's open state in
  //      main-layout, so opening the drawer no longer opens and highlights this row.
  //   2. Open = one contained .nav-group card around the row + its groups, matching Openings/Engage.
  //      Exactly one border: messaging-nav-groups' `inline` variant draws no box of its own.
  //   3. The trailing slot shows the expand chevron, swapping to the favorites star on direct hover.
  template: `
    <ng-container *ngrxLet="isMobile$ as isMobile">
      <ul class="e11-m-0 e11-p-0 e11-list-none">
        <!-- CNCT-6775: below lg the submenu expands inline, so the row + its groups become one
             contained section — the same .nav-group.active card (1px 18%-white border on a 5%-white
             fill, 8px radius) that Openings/Engage get when open. Only ONE border in play: the groups
             inside draw no box of their own. Above lg the submenu lives in its own slide-out column,
             so no card here. -->
        <li class="nav-group e11-mb-1" [ngClass]="{ active: isMessagingMenuOpen && !iconOnly && isMobile }">
          <div
            data-tour-id="nav-messages"
            (click)="toggleMessagingMenu(isMobile)"
            class="nav-link e11-flex e11-items-center e11-h-12 e11-px-2 e11-cursor-pointer"
            [ngClass]="{ active: isMessagingMenuOpen && !iconOnly, 'e11-justify-center': iconOnly }"
            [attr.role]="!iconOnly && isMobile ? 'button' : null"
            [attr.aria-expanded]="!iconOnly && isMobile ? isMessagingMenuOpen : null"
          >
            <span class="material-symbols-outlined" [ngClass]="{ 'md-24': iconOnly, 'e11-mr-2 md-18': !iconOnly }"> mail </span>
            <span class="e11-relative" *ngIf="!iconOnly">
              {{ 'Messages' | translate }}
              <e11-badge
                *ngIf="unreadConversationCount$ | async"
                [color]="'error'"
                [limit]="UNREAD_LIMIT"
                [posAdjustX]="'-4px'"
                [posAdjustY]="'-11px'"
                [size]="'xs'"
                [value]="(unreadConversationCount$ | async) ?? 0"
              ></e11-badge>
            </span>
            <ng-container *ngIf="!iconOnly">
              <span class="e11-flex-1"></span>
              <ng-container *ngIf="{ isFavorite: isFavorite$ | async } as star">
                <!-- CNCT-6775: one trailing slot, two affordances. Inline (below lg) the resting icon is
                     the expand chevron — every other expandable row has one — and the pin/unpin star
                     (CNCT-6764) takes over while the pointer is directly over the slot. Above lg there is
                     no inline expand, so the slot keeps the row-hover star it has always had. -->
                <span
                  class="nav-trailing-slot e11-shrink-0"
                  [ngClass]="{ 'nav-trailing-slot-toggle': isMobile, 'nav-trailing-slot-pinned': isMobile && star.isFavorite }"
                >
                  <span
                    *ngIf="isMobile"
                    class="material-symbols-outlined nav-chevron"
                    [ngClass]="{ 'nav-chevron-open': isMessagingMenuOpen }"
                    aria-hidden="true"
                    >expand_more</span
                  >
                  <button
                    type="button"
                    class="nav-star-btn e11-flex e11-items-center e11-justify-center e11-shrink-0 e11-cursor-pointer e11-bg-transparent e11-border-0 e11-p-0"
                    [ngClass]="{ 'nav-star-active': star.isFavorite }"
                    [attr.aria-label]="star.isFavorite ? 'Remove from favorites' : 'Add to favorites'"
                    (click)="onStarClick($event)"
                  >
                    <span class="material-symbols-outlined md-18" [ngClass]="{ 'nav-star-filled': star.isFavorite }">star</span>
                  </button>
                </span>
              </ng-container>
            </ng-container>
          </div>

          <div *ngIf="!iconOnly && isMobile" [ngClass]="{ 'nav-messages-container-active': isMessagingMenuOpen }" class="nav-messages-container">
            <messaging-nav-groups [variant]="'inline'"></messaging-nav-groups>
          </div>
        </li>
      </ul>
    </ng-container>
  `,
  styles: [
    `
      /* CNCT-6905: block host, for the same WebKit reason as messaging-nav-groups — an inline
         custom element wrapping the block-level list and its expanding section. */
      :host {
        display: block;
      }

      /* Mirrors .nav-link in app-nav-item (values from the cnect-ux reference stylesheet). */
      .nav-link {
        color: rgba(255, 255, 255, 0.65);
        border-radius: 8px;
        transition: background 0.15s ease, color 0.15s ease;
      }
      .nav-link:hover {
        color: rgba(255, 255, 255, 0.85);
        background: rgba(255, 255, 255, 0.08);
      }
      .nav-link.active {
        color: #fff;
        background: rgba(255, 255, 255, 0.1);
      }

      /* Contained card around the row + its inline submenu — mirrors .nav-group /
         .nav-group.active in app-nav-item (component-scoped there, so duplicated here). */
      .nav-group {
        border: 1px solid transparent;
        border-radius: 8px;
        transition: border-color 0.15s ease, background 0.15s ease;
      }
      .nav-group.active {
        border-color: rgba(255, 255, 255, 0.18);
        background: rgba(255, 255, 255, 0.05);
      }
      .nav-group.active > .nav-link {
        color: #fff;
      }
      /* The card supplies the row's fill; a second wash on top of it reads as a double surface. */
      .nav-group.active > .nav-link.active {
        background: transparent;
      }

      /* Expand chevron — mirrors .nav-chevron in app-nav-item. */
      .nav-chevron {
        font-size: 18px;
        color: rgba(255, 255, 255, 0.55);
        transition: transform 0.15s ease-in-out;
      }
      .nav-chevron.nav-chevron-open {
        transform: rotate(180deg);
      }

      /* Pin/unpin star — hidden until row hover/focus, always visible once favorited (mirrors
         app-nav-item). Scoped away from the toggle slot, which drives the star itself: without the
         :not(), this rule's row-level :hover outranked the slot's own and pinned the star at
         opacity 0 exactly when the pointer reached it. */
      .nav-star-btn {
        opacity: 0;
        color: rgba(255, 255, 255, 0.55);
        border-radius: 4px;
        transition: opacity 0.15s ease, color 0.15s ease;
      }
      .nav-link:hover .nav-trailing-slot:not(.nav-trailing-slot-toggle) .nav-star-btn,
      .nav-trailing-slot:not(.nav-trailing-slot-toggle) .nav-star-btn:focus-visible,
      .nav-trailing-slot:not(.nav-trailing-slot-toggle) .nav-star-btn.nav-star-active {
        opacity: 1;
      }
      .nav-star-btn:hover {
        color: #fff;
      }
      .nav-star-filled {
        font-variation-settings: 'FILL' 1;
      }

      /* CNCT-6775 — the shared trailing slot. Without the toggle (>= lg) it is a plain
         wrapper and the star behaves exactly as before. With it, chevron and star stack in
         the same 18px box: chevron at rest, star while the pointer is over the slot itself (or
         the star has keyboard focus). A pinned Messages therefore shows no filled star at rest
         here — the chevron owns the slot — but it still appears in the Favorites section above. */
      .nav-trailing-slot {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .nav-trailing-slot-toggle {
        position: relative;
        width: 18px;
        height: 18px;
      }
      .nav-trailing-slot-toggle > * {
        position: absolute;
        inset: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      /* Decorative and stacked under the star button — never let it eat the click. */
      .nav-trailing-slot-toggle .nav-chevron {
        opacity: 1;
        pointer-events: none;
        transition: opacity 0.12s ease, transform 0.15s ease-in-out;
      }
      /* Exactly one icon owns the slot at a time: the star while the pointer/focus is on the slot
         itself, or whenever Messages is pinned; the chevron the rest of the time. */
      .nav-trailing-slot-toggle:hover .nav-chevron,
      .nav-trailing-slot-toggle:focus-within .nav-chevron,
      .nav-trailing-slot-pinned .nav-chevron {
        opacity: 0;
      }
      .nav-trailing-slot-toggle:hover .nav-star-btn,
      .nav-trailing-slot-toggle:focus-within .nav-star-btn,
      .nav-trailing-slot-pinned .nav-star-btn {
        opacity: 1;
      }

      @media (prefers-reduced-motion: reduce) {
        .nav-link,
        .nav-group,
        .nav-chevron,
        .nav-star-btn {
          transition: none;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessagingNavComponent implements OnInit {
  features$ = this.configStore.features$
  @Input() isMessagingMenuOpen = false
  @Input() iconOnly = false

  isMobile$ = this.viewportService.belowBreakpoint('lg')
  unreadConversationCount$ = this.conversationStore.getUnreadConversationCountForQuery({
    types: [ConversationTypes.JobApplication, ConversationTypes.JobApplicationDM, ConversationTypes.CandidatePoolDM],
    customFilter: openConversationFilter,
  })

  UNREAD_LIMIT = 9

  isFavorite$ = this.navFavorites.isFavorite$(MESSAGES_FAVORITE_ID)

  constructor(
    private hostRef: ElementRef<HTMLElement>,
    private conversationStore: ConversationStore,
    private configStore: ConfigStore,
    private viewportService: ViewportService,
    private navigationService: EmployerNavigationService,
    private navFavorites: NavFavoritesService
  ) {}

  ngOnInit() {}

  onStarClick(event: Event): void {
    event.preventDefault()
    event.stopPropagation()
    this.navFavorites.toggleFavorite(MESSAGES_FAVORITE_ID)
  }

  toggleMessagingMenu(isMobile: boolean) {
    this.isMessagingMenuOpen = !this.isMessagingMenuOpen

    if (!isMobile) {
      this.navigationService.setExtendedNavContent(ExtendedNavId.Messaging)
      return
    }

    // CNCT-6775: inline, Messages sits low in the drawer and its groups expand BELOW the row, so on
    // a short viewport the whole section — conversations and the loading row alike — opens outside
    // the drawer's scrollport and reads as "nothing happened". Bring it back into view once the
    // max-height transition has run. `nearest` scrolls the minimum needed, so a section already in
    // view does not move.
    if (!this.isMessagingMenuOpen) return
    const section = this.hostRef.nativeElement.querySelector('.nav-group')
    if (!section) return
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    setTimeout(() => section.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' }), EXPAND_TRANSITION_MS)
  }
}
